/**
 * Client for the Farmshare Desktop device bridge.
 *
 * A station running Farmshare Desktop holds the scale open itself and serves it
 * over a loopback WebSocket. That is not merely an alternative to WebHID — on
 * such a station it is the *only* option, because HID access is exclusive: once
 * the desktop app has the device, this page cannot open it. It's also the only
 * route that will ever work for barcode scanners, which Chromium refuses to
 * expose over WebHID at all.
 *
 * `ws://127.0.0.1` is reachable from our HTTPS origin because loopback counts
 * as potentially trustworthy and is exempt from mixed-content blocking. Chrome
 * and Firefox honour that; Safari does not (WebKit #171934), so Safari simply
 * never connects here and falls through to typing.
 */
import type { ScaleReading, ScaleStatus } from './hid-pos-scale';

/**
 * Must match `BRIDGE_PORT` in `packages/desktop/src/shared/types.ts`.
 *
 * Duplicated rather than imported: the desktop app is a separate, independently
 * released artifact with no build-time relationship to the web bundle. A shop
 * can be running a station build from months ago, which is exactly why the
 * protocol below is versioned.
 */
const BRIDGE_PORT = 41777;

/** Protocol version this client understands. */
const PROTOCOL_VERSION = 2;

/**
 * Backoff between connection attempts, in ms.
 *
 * Most browsers log a failed WebSocket to the console and give the page no way
 * to suppress it, so a fixed fast retry would fill the console on every station
 * that simply doesn't run the desktop app. This stays responsive for the case
 * that matters — the operator launching the app while the page is open — then
 * settles down to a background check.
 */
const RETRY_SCHEDULE_MS = [5_000, 15_000, 30_000, 60_000, 300_000];

interface BridgeScaleReading {
  pounds: number | null;
  stable: boolean;
  status: string;
  unitLabel: string;
}

/** One scale advertised by the station. */
export interface BridgeDevice {
  id: string;
  label: string;
  transport: 'hid' | 'serial' | 'network';
  connected: boolean;
}

type BridgeMessage =
  | { v: number; type: 'hello'; stationName: string; devices: BridgeDevice[] }
  | { v: number; type: 'devices'; devices: BridgeDevice[] }
  | { v: number; type: 'scale'; deviceId: string; reading: BridgeScaleReading };

export interface BridgeHandlers {
  /** The station greeted us; the bridge is live. */
  onOpen: (stationName: string, devices: BridgeDevice[]) => void;
  /** The station's set of scales changed, or one connected/disconnected. */
  onDevices: (devices: BridgeDevice[]) => void;
  /** A weight arrived from one specific scale. */
  onReading: (deviceId: string, reading: ScaleReading) => void;
  /** The socket dropped — the desktop app quit, or was never there. */
  onClose: () => void;
}

/**
 * A reconnecting connection to the local station.
 *
 * Retries indefinitely at a slow cadence: an operator may well start Farmshare
 * Desktop after opening the packing screen, and the page shouldn't need a
 * reload to notice.
 */
export class BridgeTransport {
  private socket: WebSocket | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private attempts = 0;

  /** @param handlers - See {@link BridgeHandlers}. */
  constructor(private handlers: BridgeHandlers) {}

  /** Open the connection, retrying until {@link stop} is called. */
  start(): void {
    this.stopped = false;
    this.attempts = 0;
    this.open();
  }

  /**
   * Try again now rather than at the next backoff step: someone just opened a
   * scale or printer menu, so a station that has come up since should appear
   * without a page reload. A no-op while connected or stopped.
   */
  retryNow(): void {
    if (this.stopped || this.socket) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.attempts = 0;
    this.open();
  }

  /** Attempt one connection, scheduling a retry if it fails. */
  private open(): void {
    if (this.stopped || this.socket) return;

    let socket: WebSocket;
    try {
      socket = new WebSocket(`ws://127.0.0.1:${BRIDGE_PORT}`);
    } catch {
      // Safari throws synchronously on a mixed-content WebSocket rather than
      // firing an error event. Treat it as "no bridge here" and move on.
      this.scheduleRetry();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      // Reset the backoff so a station that drops out briefly reconnects fast.
      this.attempts = 0;
    };

    socket.onmessage = (event) => this.handleMessage(event);

    socket.onclose = () => {
      this.socket = null;
      this.handlers.onClose();
      this.scheduleRetry();
    };

    // `onerror` is always followed by `onclose`, so retry scheduling lives
    // there to avoid queuing two timers for one failure.
    socket.onerror = () => {};
  }

  /**
   * Decode one frame from the station.
   * @param event - The raw message event.
   */
  private handleMessage(event: MessageEvent): void {
    let message: BridgeMessage;
    try {
      message = JSON.parse(String(event.data));
    } catch {
      return;
    }

    // An older or newer station speaking a protocol we don't know is ignored
    // outright rather than half-read — a misparsed weight is worse than none.
    if (message.v !== PROTOCOL_VERSION) return;

    switch (message.type) {
      case 'hello':
        this.handlers.onOpen(message.stationName, message.devices);
        break;
      case 'devices':
        this.handlers.onDevices(message.devices);
        break;
      case 'scale':
        this.handlers.onReading(message.deviceId, {
          pounds: message.reading.pounds,
          stable: message.reading.stable,
          status: message.reading.status as ScaleStatus,
          unitLabel: message.reading.unitLabel,
          // The station already resolved the unit; a reading that reaches us
          // has been decoded, so there's no raw unit code left to carry.
          unitCode: message.reading.pounds === null ? -1 : 0x0c,
        });
        break;
      default:
        break;
    }
  }

  /** Queue another connection attempt. */
  private scheduleRetry(): void {
    if (this.stopped || this.retryTimer) return;
    const delay =
      RETRY_SCHEDULE_MS[Math.min(this.attempts, RETRY_SCHEDULE_MS.length - 1)];
    this.attempts += 1;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.open();
    }, delay);
  }

  /**
   * Send a message to the station, if one is connected.
   *
   * Fire-and-forget by design: everything a browser sends is a hint the station
   * is free to ignore, so a dropped socket costs nothing but the station
   * falling back to its own polling.
   * @param message - Message to send.
   * @param message.v
   * @param message.type
   * @returns Whether it went out.
   */
  send(message: { v: number; type: string }): boolean {
    const { socket } = this;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  }

  /** Stop retrying and close any open socket. */
  stop(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const { socket } = this;
    this.socket = null;
    socket?.close();
  }
}
